import React, { useState, useRef, useEffect } from 'react';
import { documentsApi, dealsApi, chatApi } from '../services/api';
import { MessageCircle, FileText, FolderOpen, Bot, User, Bookmark, X, Check } from 'lucide-react';
import './Chats.css';



const categoryOptions = [
  'Financial',
  'Legal',
  'Operational',
  'HR',
  'Commercial',
  'Risk',
  'Other'
];

function Chats() {
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showContextPicker, setShowContextPicker] = useState(false);
  
  // Save to findings modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveStep, setSaveStep] = useState('select-deal'); // 'select-deal' | 'edit-finding'
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessageIndex, setSelectedMessageIndex] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [findingCategory, setFindingCategory] = useState('Risk');
  const [findingQuestion, setFindingQuestion] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
    fetchDeals();
    const stored = localStorage.getItem('dealnexa_chats');
    if (stored) {
      const parsed = JSON.parse(stored);
      setChats(parsed);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchDocuments = async () => {
    try {
      const response = await documentsApi.list();
      const docs = (response.data.documents || []).map(doc => ({
        jobId: doc.id,
        filename: doc.filename,
        ...doc
      }));
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await dealsApi.list();
      setDeals(response.data);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const saveChats = (updatedChats) => {
    localStorage.setItem('dealnexa_chats', JSON.stringify(updatedChats));
    setChats(updatedChats);
  };

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = { type: 'user', text: inputText };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputText('');
    setIsLoading(true);

    try {
      const payload = {
        message: inputText,
        history: updatedMessages.filter(m => m.type !== 'system'),
      };

      if (activeChat?.jobId) {
        payload.jobId = activeChat.jobId;
      } else if (activeChat?.dealId) {
        payload.dealId = activeChat.dealId;
      }

      const response = await chatApi.send(payload);
      const aiMessage = { type: 'ai', text: response.data.response };
      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);

      if (activeChat) {
        const updatedChat = {
          ...activeChat,
          preview: inputText.substring(0, 50),
          messages: finalMessages,
        };
        const updatedChats = chats.map(c => c.id === activeChat.id ? updatedChat : c);
        setActiveChat(updatedChat);
        saveChats(updatedChats);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = { type: 'ai', text: 'Sorry, I encountered an error. Please try again.' };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setShowContextPicker(true);
  };

  const startChatWithContext = (type, item) => {
    let title, jobId, dealId;

    if (type === 'document') {
      title = item.filename;
      jobId = item.jobId;
    } else if (type === 'deal') {
      title = item.name;
      dealId = item.id;
    } else {
      title = 'General Chat';
    }

    const newChat = {
      id: Date.now(),
      title,
      preview: 'New conversation',
      jobId: jobId || null,
      dealId: dealId || null,
      messages: [],
    };

    const welcomeMsg = dealId
      ? { type: 'ai', text: `I'm ready to analyze the documents for "${title}". I'll read all documents linked to this deal and answer your questions based on their actual content. Ask me anything — financials, risks, key terms, red flags.` }
      : jobId
      ? { type: 'ai', text: `I'm ready to help you analyze "${title}". Ask me anything about this document.` }
      : { type: 'ai', text: "Hello! I'm your AI assistant. Select a document or deal when creating a chat for context-aware answers, or ask me anything about due diligence." };

    const updatedChats = [newChat, ...chats];
    saveChats(updatedChats);
    setActiveChat(newChat);
    setMessages([welcomeMsg]);
    setShowContextPicker(false);
  };

  const selectChat = (chat) => {
    setActiveChat(chat);
    if (chat.messages && chat.messages.length > 0) {
      setMessages(chat.messages);
    } else {
      const welcomeMsg = chat.jobId || chat.dealId
        ? { type: 'ai', text: `Continuing conversation about "${chat.title}"...` }
        : { type: 'ai', text: "Hello! Ask me anything about your documents." };
      setMessages([welcomeMsg]);
    }
  };

  const handleDeleteChat = (chatId, e) => {
    e.stopPropagation();
    const updatedChats = chats.filter(c => c.id !== chatId);
    saveChats(updatedChats);
    if (activeChat?.id === chatId) {
      setActiveChat(null);
      setMessages([]);
    }
  };

  // Save to findings functions
  const openSaveModal = (msg, idx) => {
    setSelectedMessage(msg);
    setSelectedMessageIndex(idx);
    setSaveStep('select-deal');
    setSelectedDealId('');
    setFindingCategory('Risk');
    setFindingQuestion('');
    setShowSaveModal(true);
  };

  const closeSaveModal = () => {
    setShowSaveModal(false);
    setSelectedMessage(null);
    setSelectedMessageIndex(null);
    setSelectedDealId('');
    setFindingQuestion('');
    setIsSaving(false);
  };

  const selectDealForFinding = (dealId) => {
    setSelectedDealId(dealId);
    
    // Auto-generate question from user's previous message
    let autoQuestion = '';
    if (selectedMessageIndex > 0) {
      const prevMessage = messages[selectedMessageIndex - 1];
      if (prevMessage.type === 'user') {
        autoQuestion = prevMessage.text.substring(0, 80);
        if (prevMessage.text.length > 80) {
          autoQuestion += '...';
        }
      }
    }
    
    // If no user message found, use a default
    if (!autoQuestion) {
      autoQuestion = 'AI Analysis';
    }
    
    setFindingQuestion(autoQuestion);
    setSaveStep('edit-finding');
  };

  const saveFinding = async () => {
    if (!selectedDealId || !findingQuestion.trim()) return;
    
    setIsSaving(true);
    
    try {
      const findingData = {
        date: new Date().toISOString().split('T')[0],
        category: findingCategory,
        question: findingQuestion.trim(),
        finding: selectedMessage.text,
        sourceDocument: 'AI Chat'
      };
      
      await dealsApi.createFinding(selectedDealId, findingData);
      
      closeSaveModal();
      showToast('Finding saved');
    } catch (error) {
      console.error('Failed to save finding:', error);
      alert('Failed to save finding. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const getSelectedDealName = () => {
    const deal = deals.find(d => d.id === selectedDealId);
    return deal?.name || '';
  };

  return (
    <div className="chats-container">
      <div className="chats-sidebar">
        <div className="chats-sidebar-header">
          <h3>Recent Chats</h3>
          <button className="btn-new-chat" onClick={handleNewChat}>+ New</button>
        </div>
        <div className="chats-list">
          {chats.length === 0 ? (
            <div className="chats-list-empty">
              <p>No chats yet. Click "+ New" to start.</p>
            </div>
          ) : (
            chats.map((chat) => (
              <div
                key={chat.id}
                className={`chat-list-item ${activeChat?.id === chat.id ? 'active' : ''}`}
                onClick={() => selectChat(chat)}
              >
                <span className="chat-list-icon">{chat.jobId ? <FileText size={16} /> : chat.dealId ? <FolderOpen size={16} /> : <MessageCircle size={16} />}</span>
                <div className="chat-list-info">
                  <div className="chat-list-title">{chat.title}</div>
                  <div className="chat-list-preview">{chat.preview}</div>
                </div>
                <button
                  className="chat-delete-btn"
                  onClick={(e) => handleDeleteChat(chat.id, e)}
                  title="Delete chat"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="chat-main">
        {activeChat ? (
          <>
            <div className="chat-header">
              <span className="chat-header-icon">{activeChat.jobId ? <FileText size={18} /> : activeChat.dealId ? <FolderOpen size={18} /> : <MessageCircle size={18} />}</span>
              <div>
                <span className="chat-header-title">{activeChat.title}</span>
                {activeChat.jobId && <span className="chat-context-badge">Document</span>}
                {activeChat.dealId && <span className="chat-context-badge deal">Deal</span>}
              </div>
            </div>

            <div className="chat-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className={`chat-message ${msg.type}`}>
                  <div className="message-avatar">
                    {msg.type === 'ai' ? <Bot size={14} /> : <User size={14} />}
                  </div>
                  <div className="message-content-wrapper">
                    <div className="message-content markdown-content">
                      <MarkdownRenderer text={msg.text} />
                    </div>
                    {msg.type === 'ai' && (
                      <button 
                        className="btn-save-finding"
                        onClick={() => openSaveModal(msg, idx)}
                        title="Save to Findings"
                      >
                        <Bookmark size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chat-message ai">
                  <div className="message-avatar"><Bot size={20} /></div>
                  <div className="message-content">
                    <span>Thinking</span>
                    <span className="loading-dots">...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
              <div className="chat-input-wrapper">
                <textarea
                  className="chat-input"
                  placeholder="Type a message..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  rows={1}
                />
                <button
                  className="btn-send"
                  onClick={handleSend}
                  disabled={!inputText.trim() || isLoading}
                >
                  ➤
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="chats-empty">
            <div className="chats-empty-icon"><MessageCircle size={48} /></div>
            <h3>Start a conversation</h3>
            <p>Click "+ New" to create a chat about a document or deal</p>
          </div>
        )}
      </div>

      {/* Context Picker Modal */}
      {showContextPicker && (
        <div className="context-picker-overlay" onClick={() => setShowContextPicker(false)}>
          <div className="context-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="context-picker-header">
              <h3>New Chat</h3>
              <button className="btn-close" onClick={() => setShowContextPicker(false)}>×</button>
            </div>
            <div className="context-picker-body">
              <p className="context-picker-subtitle">Select a document or deal to chat about, or start a general chat.</p>

              <button className="context-picker-option general" onClick={() => startChatWithContext('general', null)}>
                <span className="context-option-icon"><MessageCircle size={16} /></span>
                <div>
                  <div className="context-option-title">General Chat</div>
                  <div className="context-option-desc">Ask general due diligence questions</div>
                </div>
              </button>

              {documents.length > 0 && (
                <div className="context-section">
                  <h4>Documents</h4>
                  {documents.map(doc => (
                    <button
                      key={doc.jobId}
                      className="context-picker-option"
                      onClick={() => startChatWithContext('document', doc)}
                    >
                      <span className="context-option-icon"><FileText size={16} /></span>
                      <div>
                        <div className="context-option-title">{doc.filename}</div>
                        <div className="context-option-desc">Chat about this document</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {deals.length > 0 && (
                <div className="context-section">
                  <h4>Deals</h4>
                  {deals.map(deal => (
                    <button
                      key={deal.id}
                      className="context-picker-option"
                      onClick={() => startChatWithContext('deal', deal)}
                    >
                      <span className="context-option-icon"><FolderOpen size={16} /></span>
                      <div>
                        <div className="context-option-title">{deal.name}</div>
                        <div className="context-option-desc">{deal.documentCount ?? deal.documents?.length ?? 0} documents</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {documents.length === 0 && deals.length === 0 && (
                <div className="context-picker-empty">
                  <p>No documents or deals uploaded yet. Upload some documents first for context-aware chat.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save to Findings Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={closeSaveModal}>
          <div className="save-finding-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save to Findings Notes</h3>
              <button className="btn-close" onClick={closeSaveModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              {saveStep === 'select-deal' ? (
                <>
                  <p className="modal-subtitle">Select which deal to save this finding to:</p>
                  <div className="deals-list">
                    {deals.length === 0 ? (
                      <p className="no-deals">No deals available. Create a deal first.</p>
                    ) : (
                      deals.map(deal => (
                        <button
                          key={deal.id}
                          className="deal-option"
                          onClick={() => selectDealForFinding(deal.id)}
                        >
                          <FolderOpen size={18} />
                          <div className="deal-option-info">
                            <span className="deal-name">{deal.name}</span>
                            <span className="deal-docs">{deal.documentCount ?? deal.documents?.length ?? 0} documents</span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="modal-subtitle">Saving to: <strong>{getSelectedDealName()}</strong></p>
                  
                  <div className="form-group">
                    <label>Category</label>
                    <select 
                      value={findingCategory} 
                      onChange={(e) => setFindingCategory(e.target.value)}
                      className="form-select"
                    >
                      {categoryOptions.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Question / Topic</label>
                    <input
                      type="text"
                      value={findingQuestion}
                      onChange={(e) => setFindingQuestion(e.target.value)}
                      placeholder="Brief title for this finding..."
                      className="form-input"
                    />
                    <span className="input-hint">Auto-generated from your question. You can edit it.</span>
                  </div>
                  
                  <div className="form-group">
                    <label>Finding Preview</label>
                    <div className="finding-preview">
                      {selectedMessage?.text?.substring(0, 300)}
                      {selectedMessage?.text?.length > 300 && '...'}
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="modal-footer">
              {saveStep === 'edit-finding' && (
                <>
                  <button className="btn-secondary" onClick={() => setSaveStep('select-deal')}>
                    Back
                  </button>
                  <button 
                    className="btn-primary" 
                    onClick={saveFinding}
                    disabled={!findingQuestion.trim() || isSaving}
                  >
                    {isSaving ? 'Saving...' : (
                      <>
                        <Bookmark size={16} /> Save Finding
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="toast-notification">
          <Check size={16} />
          <span>{toast}</span>
        </div>
      )}
    </div>
  );
}

// Markdown Renderer Component
function MarkdownRenderer({ text }) {
  // Simple markdown parser for common patterns
  const parseMarkdown = (input) => {
    let html = input
      // Escape HTML entities
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Bold text (**text** or __text__)
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic text (*text* or _text_)
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Unordered lists (- or *)
      .replace(/^\s*[-*] (.*$)/gim, '<li>$1</li>')
      // Code blocks (inline)
      .replace(/`(.*?)`/g, '<code>$1</code>')
      // Line breaks
      .replace(/\n/g, '<br />');
    
    // Wrap consecutive li elements in ul
    html = html.replace(/(<li>.*?<\/li>)(?=(<br \/>)?(<li>|$))/gs, (match) => {
      return `<ul style="margin-left: 16px; margin-bottom: 8px;">${match}</ul>`;
    });
    
    // Fix duplicate br inside ul
    html = html.replace(/<\/ul><br \/>/g, '</ul>');
    
    return html;
  };

  const html = parseMarkdown(text);
  
  return (
    <div 
      className="markdown-body"
      dangerouslySetInnerHTML={{ __html: html }}
      style={{
        lineHeight: 1.6,
      }}
    />
  );
}

export default Chats;
